// Skill Map service (AXISREF497, AXISREF498)
// Fetches skills with their body-region tags for the Skill Manager Body Map tab,
// and previews body-part suggestions while a skill is being written.

import { acquireTokenSilent } from '../config/msalConfig';

export type SkillKind = 'prompt_rule' | 'red_flag';
export type SkillStage = 'read' | 'patient' | 'clinical' | 'funding' | 'triage' | 'route';

export interface BodyRegion {
  code: string;
  label: string;
  indici_flag: string;
  sort_order: number;
}

export interface SkillRegionTag {
  code: string;
  source: 'extracted' | 'manual';
  matched_term: string | null;
}

export interface MappedSkill {
  id: string;
  kind: SkillKind;
  name: string;
  description: string | null;
  stage: SkillStage;
  status: 'active' | 'testing' | 'inactive' | string;
  prompt_type: string | null;
  section: string | null;
  field_name: string | null;
  category: string | null;
  priority: number | null;
  updated_at: string | null;
  regions: SkillRegionTag[];
}

export interface SkillMap {
  regions: BodyRegion[];
  skills: MappedSkill[];
  extractor_version: number;
  sync: { ok: boolean; inserted: number; deleted: number; updated: number };
}

export interface BodyRegionPreview {
  regions: BodyRegion[];
  suggested: { code: string; matched_term: string | null }[];
  current: { regions: SkillRegionTag[]; has_manual: boolean } | null;
}

export const STAGES: { id: SkillStage; label: string }[] = [
  { id: 'read', label: 'Read the referral' },
  { id: 'patient', label: 'Find the patient' },
  { id: 'clinical', label: 'Understand the injury' },
  { id: 'funding', label: 'Work out funding' },
  { id: 'triage', label: 'Decide urgency' },
  { id: 'route', label: 'Send to the right place' },
];

export class SkillMapService {
  private static baseUrl =
    (import.meta.env.VITE_FUNCTION_APP_URL || 'https://func-axis-referral-dev.azurewebsites.net') + '/api';

  static async getSkillMap(): Promise<SkillMap> {
    const data = await this.request<SkillMap>('/prompt-rules/skill-map', { method: 'GET' }, 'load the skill map');
    return { ...data, regions: data.regions || [], skills: data.skills || [] };
  }

  /** Body parts suggested from a skill's description and field name, plus the saved tags when ruleId is given. */
  static async previewBodyRegions(input: {
    description?: string;
    field_name?: string;
    rule_id?: string;
  }): Promise<BodyRegionPreview> {
    const data = await this.request<BodyRegionPreview>(
      '/prompt-rules/body-regions/preview',
      { method: 'POST', body: JSON.stringify(input) },
      'suggest body parts',
    );
    return { regions: data.regions || [], suggested: data.suggested || [], current: data.current ?? null };
  }

  private static async request<T>(path: string, init: RequestInit, action: string): Promise<T> {
    const token = await this.getToken();
    const response = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      let message = `HTTP ${response.status}: Failed to ${action}`;
      try {
        const error = await response.json();
        message = error.message || error.error || message;
      } catch {
        // Response body is not JSON; keep the generic message.
      }
      throw new Error(message);
    }

    return (await response.json()) as T;
  }

  private static async getToken(): Promise<string> {
    try {
      const tokenResult = await acquireTokenSilent();
      return tokenResult.accessToken;
    } catch (error) {
      console.error('Failed to acquire token:', error);
      return '';
    }
  }
}
